import { useContext, useState, useEffect, useRef, useCallback } from "react";
import { AppContext } from "../App";
import { parseDate } from "../utils/helpers";

const DAY_NAMES = ["LUN", "MAR", "MIE", "JUE", "VIE", "SAB", "DOM"];

const computeStartDate = (availableDays, now) => {
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const cursor = new Date(today);
	let occupiedCount = 0;
	let foundAvailable = false;

	for (let i = 0; i < 92; i++) {
		if (!availableDays.includes(parseDate("yyyy-mm-dd", cursor))) {
			foundAvailable = true;
			break;
		}
		occupiedCount++;
		cursor.setDate(cursor.getDate() + 1);
	}

	if (foundAvailable && occupiedCount > 2) {
		const start = new Date(cursor);
		start.setDate(start.getDate() - 2);
		return start;
	}
	return today;
};

const DayCard = ({ date, isAvailable, isSelected, onSelect }) => {
	const dayName = DAY_NAMES[date.getDay() === 0 ? 6 : date.getDay() - 1];
	const dayNumber = date.getDate();

	const cardClasses = [
		"day-card",
		isAvailable ? "available" : "disabled",
		isSelected ? "selected" : "",
	]
		.filter(Boolean)
		.join(" ");

	const handleClick = () => {
		if (isAvailable) {
			onSelect(parseDate("yyyy-mm-dd", date));
		}
	};

	return (
		<button
			className={cardClasses}
			onClick={handleClick}
			disabled={!isAvailable}
		>
			<span className="day-name">{dayName}</span>
			<span className="day-number">{dayNumber}</span>
		</button>
	);
};

const MonthGroup = ({ monthDate, availableDays, selectedDate, startDate, onSelect }) => {
	const monthNames = [
		"Enero",
		"Febrero",
		"Marzo",
		"Abril",
		"Mayo",
		"Junio",
		"Julio",
		"Agosto",
		"Septiembre",
		"Octubre",
		"Noviembre",
		"Diciembre",
	];
	const monthName = `${monthNames[monthDate.getMonth()]} ${monthDate.getFullYear()}`;

	const monthEnd = new Date(
		monthDate.getFullYear(),
		monthDate.getMonth() + 1,
		0,
	);

	const daysInMonth = [];

	for (let d = 1; d <= monthEnd.getDate(); d++) {
		const currentDate = new Date(
			monthDate.getFullYear(),
			monthDate.getMonth(),
			d,
		);

		if (currentDate < startDate) {
			continue;
		}

		const dateStr = parseDate("yyyy-mm-dd", currentDate);
		const isAvailable = !availableDays.includes(dateStr);
		const isSelected = selectedDate === dateStr;

		daysInMonth.push(
			<DayCard
				key={dateStr}
				date={currentDate}
				isAvailable={isAvailable}
				isSelected={isSelected}
				onSelect={onSelect}
			/>,
		);
	}

	if (daysInMonth.length === 0) {
		return null;
	}

	return (
		<div className="month-group">
			<div className="month-header">{monthName}</div>
			<div className="days-row">{daysInMonth}</div>
		</div>
	);
};

export const CalendarBooking = ({ value, onDateSelected, className }) => {
	const { data, setData } = useContext(AppContext);
	const [availability, setAvailability] = useState([]);
	const [loading, setLoading] = useState(true);
	const [selectedDate, setSelectedDate] = useState(null);
	const [isDragging, setIsDragging] = useState(false);
	const scrollContainerRef = useRef(null);
	const dragState = useRef({
		isPointerDown: false,
		pointerId: null,
		startX: 0,
		startScrollLeft: 0,
		hasDragged: false,
	});
	const shouldSuppressClick = useRef(false);
	const isInitialLoad = useRef(true);

	const today = new Date();
	const monthsAhead = 3;

	const findFirstAvailableDay = useCallback(
		(unavailableDays) => {
			const now = new Date();
			const currentYear = now.getFullYear();
			const currentMonth = now.getMonth();
			const currentDay = now.getDate();

			for (let i = 0; i < monthsAhead; i++) {
				const monthDate = new Date(currentYear, currentMonth + i, 1);
				const monthEnd = new Date(
					monthDate.getFullYear(),
					monthDate.getMonth() + 1,
					0,
				);

				for (
					let d = i === 0 ? currentDay : 1;
					d <= monthEnd.getDate();
					d++
				) {
					const checkDate = new Date(
						monthDate.getFullYear(),
						monthDate.getMonth(),
						d,
					);
					const dateStr = parseDate("yyyy-mm-dd", checkDate);

					if (!unavailableDays.includes(dateStr)) {
						return dateStr;
					}
				}
			}
			return null;
		},
		[monthsAhead],
	);

	useEffect(() => {
		if (value) {
			setSelectedDate(value);
		}
	}, [value]);

	useEffect(() => {
		const fetchAvailability = async () => {
			const apiUrl =
				process.env.REACT_APP_API_URL || "http://localhost:3000";
			setLoading(true);

			try {
				const res = await fetch(
					`${apiUrl}/availability/by-day/${data.centerCode}?locator=${data.locator}`,
				);
				const response = await res.json();

				if (response.result === "ok") {
					setAvailability(response.days || []);
				}
			} catch (err) {
				console.error("Error fetching availability:", err);
			} finally {
				setLoading(false);
			}
		};

		if (data.centerCode && data.locator) {
			fetchAvailability();
		}
	}, [data.centerCode, data.locator]);

	useEffect(() => {
		if (!loading && isInitialLoad.current) {
			isInitialLoad.current = false;

			if (!selectedDate) {
				const firstAvailableDay = findFirstAvailableDay(availability);
				if (firstAvailableDay) {
					setSelectedDate(firstAvailableDay);
					setData((prevData) => ({
						...prevData,
						date: firstAvailableDay,
					}));
					onDateSelected?.(firstAvailableDay);
				}
			}
		}
	}, [
		loading,
		availability,
		selectedDate,
		findFirstAvailableDay,
		setData,
		onDateSelected,
	]);

	const handleDateSelect = (dateStr) => {
		setSelectedDate(dateStr);
		setData((prevData) => ({ ...prevData, date: dateStr }));
		onDateSelected?.(dateStr);
	};

	const handlePointerDown = (event) => {
		if (event.button !== 0 || !scrollContainerRef.current) {
			return;
		}

		dragState.current = {
			isPointerDown: true,
			pointerId: event.pointerId,
			startX: event.clientX,
			startScrollLeft: scrollContainerRef.current.scrollLeft,
			hasDragged: false,
		};
	};

	const handlePointerMove = (event) => {
		const container = scrollContainerRef.current;
		const currentDragState = dragState.current;

		if (
			!container ||
			!currentDragState.isPointerDown ||
			currentDragState.pointerId !== event.pointerId
		) {
			return;
		}

		const dragDistance = event.clientX - currentDragState.startX;

		if (Math.abs(dragDistance) > 4) {
			if (!currentDragState.hasDragged) {
				event.currentTarget.setPointerCapture?.(event.pointerId);
			}

			currentDragState.hasDragged = true;
			shouldSuppressClick.current = true;
			setIsDragging(true);
		}

		if (currentDragState.hasDragged) {
			event.preventDefault();
			container.scrollLeft =
				currentDragState.startScrollLeft - dragDistance;
		}
	};

	const stopDragging = (event) => {
		const hadDragged = dragState.current.hasDragged;

		if (
			hadDragged &&
			dragState.current.pointerId === event.pointerId &&
			event.currentTarget.hasPointerCapture?.(event.pointerId)
		) {
			event.currentTarget.releasePointerCapture?.(event.pointerId);
		}

		dragState.current.isPointerDown = false;
		dragState.current.pointerId = null;
		setIsDragging(false);

		if (hadDragged) {
			window.setTimeout(() => {
				shouldSuppressClick.current = false;
			}, 120);
		} else {
			shouldSuppressClick.current = false;
		}
	};

	const handleClickCapture = (event) => {
		if (!shouldSuppressClick.current) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		shouldSuppressClick.current = false;
	};

	if (loading) {
		return (
			<div className="loader">
				<div className="spinner"></div>
			</div>
		);
	}

	const startDate = computeStartDate(availability, today);

	const monthGroups = [];
	for (let i = 0; i < monthsAhead; i++) {
		const monthDate = new Date(
			today.getFullYear(),
			today.getMonth() + i,
			1,
		);
		monthGroups.push(
			<MonthGroup
				key={i}
				monthDate={monthDate}
				availableDays={availability}
				selectedDate={selectedDate}
				startDate={startDate}
				onSelect={handleDateSelect}
			/>,
		);
	}

	return (
		<div className={`calendar-booking ${className || ""}`}>
			<img
				src="/resources/img/gradient.png"
				alt="Gradient"
				className="calendar-gradient left"
			/>
			<div
				className={`calendar-container${isDragging ? " is-dragging" : ""}`}
				ref={scrollContainerRef}
				onPointerDown={handlePointerDown}
				onPointerMove={handlePointerMove}
				onPointerUp={stopDragging}
				onPointerCancel={stopDragging}
				onPointerLeave={stopDragging}
				onClickCapture={handleClickCapture}
			>
				{monthGroups}
			</div>
			<img
				src="/resources/img/gradient.png"
				alt="Gradient"
				className="calendar-gradient right"
			/>
		</div>
	);
};

export default CalendarBooking;
